import type { Appointment, InventoryItem } from '@clinic-os/shared/types';
import { DEMO_PATIENTS } from '../data/demo-data';

export interface MessageResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

type MessageChannel = 'sms' | 'whatsapp';

class MessagingServiceImpl {
  private get apiUrl(): string | undefined {
    return import.meta.env['VITE_PUBLIC_API_URL'] as string | undefined;
  }

  isConfigured(): boolean {
    return Boolean(this.apiUrl);
  }

  private async send(
    channel: MessageChannel,
    to: string,
    body: string
  ): Promise<MessageResult> {
    if (!this.apiUrl) {
      return {
        success: false,
        error: 'VITE_PUBLIC_API_URL not configured. Set it to the api origin (e.g. https://api.clinic-os.blueforge.studio).',
      };
    }
    try {
      const res = await fetch(`${this.apiUrl}/api/messaging/${channel}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, body }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        messageId?: string;
        error?: string;
      };
      if (res.ok && json.success) {
        return { success: true, messageId: json.messageId };
      }
      return { success: false, error: json.error ?? `HTTP ${res.status}` };
    } catch (err) {
      return { success: false, error: (err as Error).message };
    }
  }

  async sendSMS(to: string, body: string): Promise<MessageResult> {
    return this.send('sms', to, body);
  }

  async sendWhatsApp(to: string, body: string): Promise<MessageResult> {
    return this.send('whatsapp', to, body);
  }

  private truncateForSms(message: string, maxLength = 160): string {
    if (message.length <= maxLength) {
      return message;
    }
    return message.slice(0, maxLength - 3) + '...';
  }

  private formatAppointmentDateTime(scheduledAt: string): string {
    const date = new Date(scheduledAt);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Send an appointment reminder to a patient.
   */
  async sendAppointmentReminder(
    appointment: Appointment,
    patientPhone: string,
    patientName: string,
    channel: MessageChannel = 'sms'
  ): Promise<MessageResult> {
    const appointmentTime = this.formatAppointmentDateTime(appointment.scheduledAt);

    let message: string;
    if (channel === 'whatsapp') {
      message = `🏥 *Appointment Reminder*

Hi ${patientName},

This is a reminder for your upcoming appointment:

📅 *${appointment.type}*
🕐 ${appointmentTime}
⏱️ ${appointment.durationMinutes} minutes

Please arrive 10 minutes early. See you soon!`;
    } else {
      // SMS - keep it concise
      message = this.truncateForSms(
        `MedFlow: Reminder - ${patientName}, you have ${appointment.type} on ${appointmentTime}. Please arrive 10 min early.`
      );
    }

    return this.send(channel, patientPhone, message);
  }

  /**
   * Send an appointment confirmation to a patient.
   */
  async sendAppointmentConfirmation(
    appointment: Appointment,
    patientPhone: string,
    patientName: string,
    channel: MessageChannel = 'sms'
  ): Promise<MessageResult> {
    const appointmentTime = this.formatAppointmentDateTime(appointment.scheduledAt);

    let message: string;
    if (channel === 'whatsapp') {
      message = `✅ *Appointment Confirmed*

Hi ${patientName},

Your appointment has been confirmed:

📅 *${appointment.type}*
🕐 ${appointmentTime}
⏱️ ${appointment.durationMinutes} minutes

We look forward to seeing you!`;
    } else {
      message = this.truncateForSms(
        `MedFlow: ${patientName}, your ${appointment.type} appointment is confirmed for ${appointmentTime}.`
      );
    }

    return this.send(channel, patientPhone, message);
  }

  /**
   * Send a low stock alert to the clinic.
   */
  async sendLowStockAlert(
    item: InventoryItem,
    clinicPhone: string
  ): Promise<MessageResult> {
    const message = `⚠️ *Low Stock Alert*

${item.name} (${item.sku || 'No SKU'}) is running low:

📦 Current: ${item.quantity} ${item.unit || 'units'}
🔴 Threshold: ${item.lowStockThreshold} ${item.unit || 'units'}

Please reorder soon.`;

    return this.send('sms', clinicPhone, message);
  }

  /**
   * Send an invoice to a patient via SMS or WhatsApp.
   */
  async sendInvoice(
    invoiceId: string,
    patientPhone: string,
    paymentLink: string,
    channel: MessageChannel = 'sms'
  ): Promise<MessageResult> {
    let message: string;
    if (channel === 'whatsapp') {
      message = `📄 *Invoice Ready*

Your invoice #${invoiceId} is ready.

💳 Pay now: ${paymentLink}

Thank you for choosing us!`;
    } else {
      // SMS - keep it very short with the link
      message = this.truncateForSms(
        `MedFlow: Your invoice #${invoiceId} is ready. Pay now: ${paymentLink}`
      );
    }

    return this.send(channel, patientPhone, message);
  }

  /**
   * Get patient phone from patient ID using demo data.
   * In production, this would query the actual patient record.
   */
  getPatientPhone(patientId: string): string | undefined {
    const patient = DEMO_PATIENTS.find((p) => p.id === patientId);
    return patient?.phone;
  }

  /**
   * Get patient name from patient ID using demo data.
   */
  getPatientName(patientId: string): string {
    const patient = DEMO_PATIENTS.find((p) => p.id === patientId);
    return patient ? `${patient.firstName} ${patient.lastName}` : `Patient #${patientId}`;
  }
}

export const messagingService = new MessagingServiceImpl();
